
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Calendar, DollarSign, Users, Clock, Settings } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';
import { VenueDetailModal } from '@/components/venue/VenueDetailModal';
import { venueAnalyticsService, type VenueAnalytics } from '@/services/venueAnalyticsService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { SeasonArchiveManager } from '@/components/retention/SeasonArchiveManager';
import { EntityRolloverScheduler } from '@/components/seasons/EntityRolloverScheduler';
import { NotificationAnalyticsChart } from '@/components/notifications/NotificationAnalyticsChart';
import { HistoricalPerformanceComparison } from '@/components/analytics/HistoricalPerformanceComparison';
import { RevenueAnalyticsDashboard } from '@/components/analytics/RevenueAnalyticsDashboard';
import { QuickActionCards } from '@/components/dashboard/QuickActionCards';
import { supabase } from '@/integrations/supabase/client';

const VenueDashboard: React.FC = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const currentUser = (internalUser && !supabaseUser) ? internalUser : 
                     (!internalUser && supabaseUser) ? supabaseUser :
                     internalUser ? internalUser : supabaseUser;

  const [analytics, setAnalytics] = useState<VenueAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState<'venues' | 'bookings' | 'revenue' | 'schedule' | 'management' | 'analytics' | null>(null);
  const [venueIds, setVenueIds] = useState<string[]>([]);

  useEffect(() => {
    if (currentUser) {
      loadAnalytics();
      loadVenueIds();
    }
  }, [currentUser]);

  const loadAnalytics = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const data = await venueAnalyticsService.getVenueAnalytics(currentUser.id);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading venue analytics:', error);
      toast.error('Failed to load venue analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadVenueIds = async () => {
    if (!currentUser) return;
    
    try {
      const result: any = await supabase
        .from('venues')
        .select('venue_id');
      
      setVenueIds(result.data?.map((v: any) => v.venue_id) || []);
    } catch (error) {
      console.error('Error loading venue IDs:', error);
    }
  };

  const handleSectionClick = (section: 'venues' | 'bookings' | 'revenue' | 'schedule' | 'management' | 'analytics') => {
    setSelectedSection(section);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {[1, 2].map((i) => (
              <div key={i} className="h-64 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <PermissionGate permission="manage_venue">
        <QuickActionCards 
          userRole="venue"
          onCreateEvent={() => console.log('Create event')}
          onManageBookings={() => console.log('Manage bookings')}
        />
      </PermissionGate>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <PermissionGate permission="manage_venue">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
            onClick={() => handleSectionClick('venues')}
          >
            <CardContent className="p-4 flex items-center space-x-4">
              <MapPin className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Active Venues</h4>
                <p className="text-sm text-gray-300">{analytics?.activeVenues || 0} locations</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="manage_bookings">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
            onClick={() => handleSectionClick('bookings')}
          >
            <CardContent className="p-4 flex items-center space-x-4">
              <Calendar className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Today's Bookings</h4>
                <p className="text-sm text-gray-300">{analytics?.todaysBookings || 0} scheduled</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_analytics">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
            onClick={() => handleSectionClick('revenue')}
          >
            <CardContent className="p-4 flex items-center space-x-4">
              <DollarSign className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Monthly Revenue</h4>
                <p className="text-sm text-gray-300">${analytics?.monthlyRevenue?.toLocaleString() || 0}</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PermissionGate permission="manage_bookings">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
            onClick={() => handleSectionClick('schedule')}
          >
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-sports-green" />
                Today's Schedule
              </h4>
              <div className="space-y-3">
                {analytics?.todaysSchedule && analytics.todaysSchedule.length > 0 ? (
                  analytics.todaysSchedule.slice(0, 3).map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-gray-700 rounded-md">
                      <div>
                        <span className="text-white font-medium">{item.field}</span>
                        <p className="text-sm text-gray-300">{item.teams}</p>
                      </div>
                      <span className="text-sports-green">{item.time}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-400">
                    <p>No events scheduled for today</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="manage_venue">
          <Card 
            className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
            onClick={() => handleSectionClick('management')}
          >
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-sports-green" />
                Venue Management
              </h4>
              <div className="space-y-3">
                {analytics?.venueUtilization && analytics.venueUtilization.length > 0 ? (
                  analytics.venueUtilization.map((venue, index) => (
                    <div key={index} className="bg-gray-700 p-4 rounded-lg">
                      <h5 className="font-medium text-white mb-2">{venue.name}</h5>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-300">Utilization</span>
                        <span className={`${
                          venue.utilization >= 80 ? 'text-sports-green' :
                          venue.utilization >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {venue.utilization}%{venue.status ? ` - ${venue.status}` : ''}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-400">
                    <p>No venue data available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <PermissionGate permission="view_analytics">
        <Card 
          className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
          onClick={() => handleSectionClick('analytics')}
        >
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-sports-green" />
              Customer Analytics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {analytics?.customerAnalytics?.regularCustomers || 0}
                </div>
                <div className="text-sm text-gray-300">Regular Customers</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {analytics?.customerAnalytics?.satisfactionRate || 0}%
                </div>
                <div className="text-sm text-gray-300">Satisfaction Rate</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {analytics?.customerAnalytics?.averageRating || 0}
                </div>
                <div className="text-sm text-gray-300">Average Rating</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>

      {/* Season Management Section */}
      <PermissionGate permission="manage_venue">
        {currentUser && venueIds.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SeasonArchiveManager 
              entityId={venueIds[0]} 
              entityType="venue"
            />
            <EntityRolloverScheduler
              entityType="venue"
              entityIds={venueIds}
              entityName="My Venues"
            />
          </div>
        )}
      </PermissionGate>

      {/* Detail Modal */}
      <VenueDetailModal
        isOpen={!!selectedSection}
        onClose={() => setSelectedSection(null)}
        section={selectedSection!}
        data={analytics}
      />
    </div>
  );
};

export default VenueDashboard;
